import React, { useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Dialog, Checkbox, Select } from 'Components/ui';
import { updatePolicyVisibility } from 'services/transparency-config-service';
import { getPolicies } from 'services/policies-service';
import { DotsLoader } from 'Components/DotsLoader';
import styled from 'styled-components';
import { Skeleton } from 'Components/common/Skeleton';

const Card = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`

const Title = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 4px;
`

const Subtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0 0 16px;
  line-height: 1.6;
`

const Divider = styled.div`
  height: 1px;
  background: var(--vo-cloud);
  margin: 20px 0;
`

const LegendBox = styled.div`
  background: var(--vo-mist);
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 20px;
`

const LegendTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: var(--vo-ink-2);
  margin: 0 0 10px;
`

const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const LegendBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 2px 10px;
  border-radius: 20px;
`

const LegendDesc = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
`

const TableBorder = styled.div`
  border: 1px solid var(--vo-cloud);
  border-radius: 10px;
  overflow: hidden;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const TableHead = styled.thead`
  background: var(--vo-mist);
`

const TableBody = styled.tbody``

const TableRow = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  &:last-child { border-bottom: none; }
  &:hover { background: var(--vo-capri-25); }
`

const TableCell = styled.td`
  padding: 12px 16px;
  vertical-align: middle;
  text-align: ${({ $align }) => $align || 'left'};
`

const TableHeaderCell = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  padding: 12px 16px;
  text-align: ${({ $align }) => $align || 'left'};
  border-bottom: 1px solid var(--vo-cloud);
  white-space: nowrap;
`

const HeaderMultiline = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  padding: 12px 16px;
  text-align: center;
  border-bottom: 1px solid var(--vo-cloud);
`

const HeaderSub = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 10px;
  color: var(--vo-steel);
  margin: 2px 0 0;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
`

const PolicyName = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 2px;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`

const SaveButton = styled.button`
  height: 44px;
  background: var(--vo-celeste);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover { background: var(--vo-cobalto); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

const Alert = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Lato', sans-serif;
`

const AlertError = styled(Alert)`
  background-color: var(--vo-danger-10);
  color: var(--vo-danger);
`

const AlertInfo = styled(Alert)`
  background-color: var(--vo-info-8);
  color: var(--vo-celeste);
`

export const PolicyVisibility = ({ config, onUpdate, companyKey }) => {
  const { t } = useTranslation(['transparency', 'common']);

  const { data: policiesData, isLoading: loadingPolicies, error: policiesError } = useQuery(
    ['policies', companyKey],
    () => getPolicies(companyKey),
    {
      enabled: !!companyKey,
      staleTime: 5 * 60 * 1000,
    }
  );

  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      policies: [],
    },
  });

  const { fields } = useFieldArray({
    control,
    name: 'policies',
  });

  useEffect(() => {
    if (policiesData?.data && Array.isArray(policiesData.data)) {
      const savedConfig = Array.isArray(config?.policyVisibility)
        ? config.policyVisibility
        : [];

      const enriched = policiesData.data.map((policy) => {
        const existing = savedConfig.find(s => s.policyId === policy._id);
        return {
          policyId: policy._id,
          policyName: policy.name,
          visibility: existing?.visibility || 'hidden',
          showExecutiveSummary: existing?.showExecutiveSummary ?? false,
          showFullContent: existing?.showFullContent ?? false,
        };
      });

      setValue('policies', enriched);
    }
  }, [policiesData, config, setValue]);

  const mutation = useMutation(
    (data) => updatePolicyVisibility(companyKey, data.policies),
    {
      onSuccess: () => {
        Dialog.alert({
          title: t('transparency:policyVisibility.notifications.success'),
          message: t('transparency:policyVisibility.notifications.successText'),
          icon: 'success',
        });
        onUpdate();
      },
      onError: (error) => {
        Dialog.alert({
          title: t('transparency:policyVisibility.notifications.error'),
          message: error.response?.data?.message || error.message,
          icon: 'error',
        });
      },
    }
  );

  const onSubmit = (data) => {
    mutation.mutate(data);
  };

  const isExecutiveSummaryEnabled = (visibility) =>
    visibility === 'partial' || visibility === 'protected';

  const isFullContentEnabled = (visibility) =>
    visibility === 'protected';

  if (loadingPolicies) {
    return (
      <Card>
        <Skeleton $width={300} $height={40} $marginBottom={8} />
        <Skeleton $width={'100%'} $height={20} $marginBottom={16} />
        <Skeleton $width={'100%'} $height={200} />
      </Card>
    );
  }

  if (policiesError || !companyKey) {
    return (
      <Card>
        <AlertError>{t('transparency:policyVisibility.error')}</AlertError>
      </Card>
    );
  }

  if (!policiesData?.data || policiesData.data.length === 0) {
    return (
      <Card>
        <Title>{t('transparency:policyVisibility.title')}</Title>
        <AlertInfo>{t('transparency:policyVisibility.empty')}</AlertInfo>
      </Card>
    );
  }

  return (
    <Card>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Title>{t('transparency:policyVisibility.title')}</Title>
        <Subtitle>{t('transparency:policyVisibility.subtitle')}</Subtitle>

        <LegendBox>
          <LegendTitle>{t('transparency:policyVisibility.legend.title')}</LegendTitle>
          <LegendRow>
            <LegendItem>
              <LegendBadge $color="var(--vo-success)" $bg="var(--vo-success-10)">{t('transparency:policyVisibility.levels.public')}</LegendBadge>
              <LegendDesc>{t('transparency:policyVisibility.legend.publicDesc')}</LegendDesc>
            </LegendItem>
            <LegendItem>
              <LegendBadge $color="var(--vo-warning)" $bg="var(--vo-warning-10)">{t('transparency:policyVisibility.levels.partial')}</LegendBadge>
              <LegendDesc>{t('transparency:policyVisibility.legend.partialDesc')}</LegendDesc>
            </LegendItem>
            <LegendItem>
              <LegendBadge $color="var(--vo-celeste)" $bg="var(--vo-info-10)">{t('transparency:policyVisibility.levels.protected')}</LegendBadge>
              <LegendDesc>{t('transparency:policyVisibility.legend.protectedDesc')}</LegendDesc>
            </LegendItem>
            <LegendItem>
              <LegendBadge $color="var(--vo-slate)" $bg="var(--vo-slate-10)">{t('transparency:policyVisibility.levels.hidden')}</LegendBadge>
              <LegendDesc>{t('transparency:policyVisibility.legend.hiddenDesc')}</LegendDesc>
            </LegendItem>
          </LegendRow>
        </LegendBox>

        <Divider />

        <TableBorder>
          <Table>
            <TableHead>
              <tr>
                <TableHeaderCell>{t('transparency:policyVisibility.table.policy')}</TableHeaderCell>
                <TableHeaderCell $align="center">{t('transparency:policyVisibility.table.visibility')}</TableHeaderCell>
                <HeaderMultiline>
                  <span>{t('transparency:policyVisibility.table.showSummary')}</span>
                  <HeaderSub>{t('transparency:policyVisibility.table.summarySub')}</HeaderSub>
                </HeaderMultiline>
                <HeaderMultiline>
                  <span>{t('transparency:policyVisibility.table.showContent')}</span>
                  <HeaderSub>{t('transparency:policyVisibility.table.contentSub')}</HeaderSub>
                </HeaderMultiline>
              </tr>
            </TableHead>
            <TableBody>
              {fields.map((field, index) => {
                const currentVisibility = watch(`policies.${index}.visibility`);
                const summaryEnabled = isExecutiveSummaryEnabled(currentVisibility);
                const contentEnabled = isFullContentEnabled(currentVisibility);

                return (
                  <TableRow key={field.id}>
                    <TableCell>
                      <PolicyName>{field.policyName}</PolicyName>
                    </TableCell>
                    <TableCell $align="center">
                      <Controller
                        control={control}
                        name={`policies.${index}.visibility`}
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name={`policies.${index}.visibility`}
                            value={value}
                            onChange={onChange}
                            options={[
                              { value: 'public', label: t('transparency:policyVisibility.levels.public') },
                              { value: 'partial', label: t('transparency:policyVisibility.levels.partial') },
                              { value: 'protected', label: t('transparency:policyVisibility.levels.protected') },
                              { value: 'hidden', label: t('transparency:policyVisibility.levels.hidden') },
                            ]}
                            fullWidth={false}
                            sx={{ width: 130, '.MuiOutlinedInput-input': { padding: '6px 10px', fontSize: 12 } }}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell $align="center">
                      <Controller
                        control={control}
                        name={`policies.${index}.showExecutiveSummary`}
                        render={({ field: { onChange, value } }) => (
                          <Checkbox checked={value} onChange={onChange} disabled={!summaryEnabled} />
                        )}
                      />
                    </TableCell>
                    <TableCell $align="center">
                      <Controller
                        control={control}
                        name={`policies.${index}.showFullContent`}
                        render={({ field: { onChange, value } }) => (
                          <Checkbox checked={value} onChange={onChange} disabled={!contentEnabled} />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableBorder>

        <Actions>
          <SaveButton type="submit" disabled={mutation.isLoading}>
            {mutation.isLoading ? <DotsLoader /> : t('transparency:policyVisibility.actions.save')}
          </SaveButton>
        </Actions>
      </form>
    </Card>
  );
};
